import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActionLinkPicker } from "@/components/workspace/ActionLinkPicker";
import { PersonSelect, ReviewFields, reviewPayload } from "@/components/workspace/team/WorkFormParts";
import { WEEKDAYS } from "@/lib/workspace";
import { toast } from "sonner";

export function ResponsibilityDialog({ orgId, people, onClose, onSaved }) {
  const [f, setF] = useState({
    title: "", description: "", owner_id: "", cadence: "weekly", weekday: "4", day_of_month: "1", start_date: "", end_date: "",
    link: { link_type: "kpi", link_id: "" }, requires_review: false, reviewer_id: "", alternate_reviewer_id: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    const dom = Number(f.day_of_month);
    if (!f.title.trim()) return toast.error("Give the responsibility a title");
    if (!f.link.link_id) return toast.error("Choose the priority, improvement or KPI it supports");
    if (!f.owner_id) return toast.error("Choose who is responsible");
    if (f.cadence === "monthly" && !(dom >= 1 && dom <= 28)) return toast.error("Choose a day of the month between 1 and 28");
    if (f.requires_review && !f.reviewer_id) return toast.error("Choose an independent reviewer");
    setSaving(true);
    try {
      const { data } = await api.post(`/workspace/responsibilities?org_id=${orgId}`, {
        title: f.title.trim(), description: f.description || null, owner_id: f.owner_id, cadence: f.cadence,
        weekday: Number(f.weekday), day_of_month: dom || 1, start_date: f.start_date || null, end_date: f.end_date || null,
        ...f.link, ...reviewPayload(f),
      });
      toast.success(`Recurring responsibility set up${data.generated_now ? ` — ${data.generated_now} task${data.generated_now === 1 ? "" : "s"} scheduled` : ""}`);
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="responsibility-dialog">
        <DialogHeader>
          <DialogTitle>New recurring responsibility</DialogTitle>
          <DialogDescription>Compass creates one dated task per period automatically — never duplicates, never back-fills more than 8.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1" placeholder="e.g. Weekly sales pipeline update" data-testid="resp-title" /></div>
          <div><Label>Details</Label><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1" data-testid="resp-description" /></div>
          <ActionLinkPicker orgId={orgId} value={f.link} onChange={(v) => set("link", v)} label="What does it support?" testId="resp-link" />
          <PersonSelect label="Responsible person" value={f.owner_id} onChange={(v) => set("owner_id", v)} people={people.filter((p) => p.in_scope)} testId="resp-owner" />
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>How often</Label>
              <Select value={f.cadence} onValueChange={(v) => set("cadence", v)}>
                <SelectTrigger className="mt-1" data-testid="resp-cadence"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="weekly">Every week</SelectItem><SelectItem value="monthly">Every month</SelectItem></SelectContent>
              </Select>
            </div>
            {f.cadence === "weekly" ? (
              <div><Label>Due on</Label>
                <Select value={f.weekday} onValueChange={(v) => set("weekday", v)}>
                  <SelectTrigger className="mt-1" data-testid="resp-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div><Label>Day of the month (1–28)</Label><Input type="number" min="1" max="28" value={f.day_of_month} onChange={(e) => set("day_of_month", e.target.value)} className="mt-1" data-testid="resp-dom" /></div>
            )}
            <div><Label>Starts</Label><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1" data-testid="resp-start" /></div>
            <div><Label>Ends (optional)</Label><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1" data-testid="resp-end" /></div>
          </div>
          <ReviewFields f={f} set={set} people={people} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="resp-save-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Set up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
